import { useResponsive } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { SimplePublicApiApp } from '@/models/App';

import AppListItem from './AppListItem';

type AppListProps = {
  apps: SimplePublicApiApp[];
  isOpen: boolean;
  onFocusReset?: () => void;
} & BoxProps;

const AppList = ({ apps, isOpen, onFocusReset, ...props }: AppListProps) => {
  const [focusId, setFocusId] = useState(-1);
  const { isMobile } = useResponsive();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      let newFocusId = focusId;

      if (e.code === 'ArrowDown') {
        e.preventDefault();
        newFocusId = focusId === apps.length - 1 ? -1 : focusId + 1;
      } else if (e.code === 'ArrowUp') {
        e.preventDefault();
        newFocusId = focusId <= 0 ? -1 : focusId - 1;
      }

      setFocusId(newFocusId);

      if (newFocusId === -1 && onFocusReset) {
        onFocusReset();
      }
    },
    [apps.length, focusId, isOpen, onFocusReset],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    setFocusId(-1);
  }, [apps]);

  if (apps.length === 0) {
    return (
      <Box display="flex" paddingY="8" paddingX="16" gap="8" width="320px">
        <Text textStyle="body/lg/regular" color="text/secondary">
          No matching projects found.
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" data-testid="AppList" {...props}>
      {apps.map((app, idx) => (
        <Fragment key={app.slug}>
          {!!idx && !isMobile && <Separator />}
          <AppListItem {...app} shouldFocus={idx === focusId} />
        </Fragment>
      ))}
    </Box>
  );
};

export default AppList;
